import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Phone, RotateCcw, ShieldCheck, Sparkles } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/primitives";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp } = useAuthStore();

  const [step, setStep] = React.useState<"phone" | "otp">("phone");
  const [phone, setPhone] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [devOtp, setDevOtp] = React.useState<string | null>(null);
  const [countdown, setCountdown] = React.useState(0);

  // Countdown timer for resend OTP
  React.useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const cleanPhone = (val: string) => val.replace(/\D/g, "");

  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const digits = cleanPhone(phone);

    if (digits.length < 8) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await sendOtp(digits);
      toast.success("Verification code sent successfully!");
      if (res.otp) {
        setDevOtp(res.otp);
      }
      setStep("otp");
      setCountdown(30);
      setOtp("");
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : "Failed to send verification code";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanCode = otp.trim();

    if (!cleanCode || cleanCode.length < 4) {
      setError("Please enter the verification code");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const digits = cleanPhone(phone);
      await verifyOtp(digits, cleanCode);
      toast.success("Welcome back, Administrator!");
      navigate("/", { replace: true });
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : "Invalid verification code";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-xl">
          {/* Top subtle decorative gradient */}
          <div className="pointer-events-none absolute -left-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />

          {/* Header */}
          <div className="mb-7 text-center">
            <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-inner">
              <ShieldCheck className="h-7 w-7" />
            </span>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">GetPet Admin</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {step === "phone"
                ? "Enter your registered mobile number to sign in"
                : "Enter the verification code sent to your phone"}
            </p>
          </div>

          {/* Step 1: Enter Phone Number */}
          {step === "phone" && (
            <form onSubmit={handleSendOtp} className="space-y-4" noValidate>
              <Field
                label="Mobile Number"
                htmlFor="admin-phone"
                required
                error={error}
                hint="Your registered admin account phone number"
              >
                <div className="relative flex items-center">
                  <div className="absolute left-3 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>+91</span>
                    <span className="text-border">|</span>
                  </div>
                  <Input
                    id="admin-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    autoFocus
                    placeholder="98765 43210"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setError(null);
                    }}
                    className="h-11 pl-20 text-base font-medium tracking-wide"
                    disabled={busy}
                  />
                </div>
              </Field>

              <Button
                type="submit"
                className="h-11 w-full text-sm font-medium shadow-sm transition-all"
                loading={busy}
                disabled={cleanPhone(phone).length < 8}
              >
                Send Verification OTP
              </Button>
            </form>
          )}

          {/* Step 2: Enter OTP */}
          {step === "otp" && (
            <form onSubmit={handleVerifyOtp} className="space-y-5" noValidate>
              <div className="flex items-center justify-between rounded-lg border border-border bg-surface/60 px-3 py-2 text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Phone className="h-3.5 w-3.5 text-primary" />
                  <span className="font-medium text-foreground">+91 {phone}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setError(null);
                    setDevOtp(null);
                  }}
                  className="flex items-center gap-1 text-primary hover:underline font-medium"
                >
                  <ArrowLeft className="h-3 w-3" /> Change
                </button>
              </div>

              {/* Dev mode helper badge */}
              {devOtp && (
                <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary animate-in fade-in">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Dev OTP: <strong className="font-mono text-sm tracking-wider">{devOtp}</strong></span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOtp(devOtp);
                      setError(null);
                    }}
                    className="rounded bg-primary/20 px-2 py-0.5 font-semibold text-primary hover:bg-primary/30 transition-colors"
                  >
                    Auto-Fill
                  </button>
                </div>
              )}

              <Field
                label="Enter Verification Code"
                htmlFor="otp-code"
                required
                error={error}
              >
                <div className="relative flex items-center">
                  <div className="absolute left-3 text-muted-foreground">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <Input
                    id="otp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtp(val);
                      setError(null);
                      if (val.length === 4 || val.length === 6) {
                        // Optional auto-submit can be triggered if desired
                      }
                    }}
                    className="h-12 pl-10 text-center font-mono text-xl tracking-[0.3em]"
                    disabled={busy}
                  />
                </div>
              </Field>

              <Button
                type="submit"
                className="h-11 w-full text-sm font-medium shadow-sm transition-all"
                loading={busy}
                disabled={otp.trim().length < 4}
              >
                Verify &amp; Sign In
              </Button>

              <div className="flex items-center justify-between pt-1 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setError(null);
                    setDevOtp(null);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  &larr; Back to phone
                </button>

                {countdown > 0 ? (
                  <span className="text-muted-foreground">
                    Resend code in <span className="font-semibold text-foreground">{countdown}s</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSendOtp()}
                    disabled={busy}
                    className="flex items-center gap-1 font-semibold text-primary hover:underline"
                  >
                    <RotateCcw className="h-3 w-3" /> Resend OTP
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        <p className="mt-5 text-center text-[11px] text-muted-foreground/60">
          GetPet Admin Portal · Authorised personnel only
        </p>
      </div>
    </div>
  );
};
